'use client';

import { resolvePath, resolveText } from '@macaron-a2ui/core/v0_8';
import type { MacaronRenderContext, RecordAny } from '@macaron-a2ui/core/v0_8';

function toDatetimeLocalValue(value: string): string {
  if (!value) return '';
  const directMatch = value.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
  if (directMatch) return directMatch[1] || '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getFullYear().toString().padStart(4, '0');
  const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
  const day = `${parsed.getDate()}`.padStart(2, '0');
  const hours = `${parsed.getHours()}`.padStart(2, '0');
  const minutes = `${parsed.getMinutes()}`.padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toDateValue(value: string): string {
  if (!value) return '';
  const directMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
  if (directMatch) return directMatch[1] || '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getFullYear().toString().padStart(4, '0');
  const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
  const day = `${parsed.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toTimeValue(value: string): string {
  if (!value) return '';
  const directMatch = value.match(/(\d{2}:\d{2})/);
  if (directMatch) return directMatch[1] || '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const hours = `${parsed.getHours()}`.padStart(2, '0');
  const minutes = `${parsed.getMinutes()}`.padStart(2, '0');
  return `${hours}:${minutes}`;
}

function normalizeOutputValue(inputValue: string, enableDate: boolean, enableTime: boolean): string {
  if (!inputValue) return '';
  if (enableDate && !enableTime) return inputValue;
  if (!enableDate && enableTime) return /^\d{2}:\d{2}$/.test(inputValue) ? `${inputValue}:00` : inputValue;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(inputValue) ? `${inputValue}:00` : inputValue;
}

function resolveInputType(enableDate: boolean, enableTime: boolean) {
  if (enableDate && !enableTime) return 'date';
  if (!enableDate && enableTime) return 'time';
  return 'datetime-local';
}

function resolveDisplayValue(value: string, enableDate: boolean, enableTime: boolean) {
  if (enableDate && !enableTime) return toDateValue(value);
  if (!enableDate && enableTime) return toTimeValue(value);
  return toDatetimeLocalValue(value);
}

function resolvePlaceholder(enableDate: boolean, enableTime: boolean) {
  if (enableDate && enableTime) return 'Select a date and time';
  if (enableDate) return 'Select a date';
  if (enableTime) return 'Select a time';
  return 'Select a date/time';
}

function resolveMinMax(value: unknown, enableDate: boolean, enableTime: boolean) {
  if (typeof value !== 'string' || !enableDate) return undefined;
  if (enableDate && !enableTime) return toDateValue(value) || undefined;
  return toDatetimeLocalValue(value) || undefined;
}

export function MacaronDateTimeInput({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const enableDate = typeof config.enableDate === 'boolean' ? config.enableDate : true;
  const enableTime = typeof config.enableTime === 'boolean' ? config.enableTime : true;
  const valuePath = resolvePath(config.value);
  const value = resolveText(config.value, ctx.data);
  const type = resolveInputType(enableDate, enableTime);
  const displayValue = resolveDisplayValue(value, enableDate, enableTime);
  const min = resolveMinMax(config.firstDate, enableDate, enableTime);
  const max = resolveMinMax(config.lastDate, enableDate, enableTime);

  return (
    <input
      type={type}
      className="w-full rounded-lg border border-[#D0D5DD] bg-white px-3 py-2 text-sm"
      value={displayValue}
      min={min}
      max={max}
      placeholder={resolvePlaceholder(enableDate, enableTime)}
      onChange={(event) => {
        if (!valuePath) return;
        ctx.updateData?.(valuePath, normalizeOutputValue(event.target.value, enableDate, enableTime));
      }}
    />
  );
}
